import type { IEmployeeCoverage } from '@sharedInterfaces/IOpportunity';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CardBox from '@src/App/Components/Cards/CardBox/CardBox';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import './SuggestedEmployeesBox.css';
import List from '@src/App/Components/Lists/List/List';

import { EmployeeSuggestions } from '../../General/EmployeeSuggestion/EmployeeSuggestions/EmployeeSuggestions';

interface SuggestedEmployeesBoxProps {
    title?: string;
    suggestedEmployees: IEmployeeCoverage[] | null;
}

/**
 * Renders a component that displays suggested employees in a box.
 *
 * @param {SuggestedEmployeesBoxProps} props - The properties for the SuggestedEmployeesBox component.
 * @param {string} props.title - The title for the suggested employees box.
 * @param {Array<Employee>} props.suggestedEmployees - The array of suggested employees.
 *
 * @returns {JSX.Element} The rendered SuggestedEmployeesBox component.
 */
function SuggestedEmployeesBox({ title, suggestedEmployees }: SuggestedEmployeesBoxProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;

    const newTitle = title ? title : langStrings.suggestedEmployee;

    const innerSuggestedEmployees = suggestedEmployees?.slice();
    innerSuggestedEmployees?.sort((a, b) => {
        const diff = b.score - a.score;
        if (diff !== 0) return diff;
        return a.title.localeCompare(b.title);
    });

    return (
        <CardBox title={newTitle}>
            <List>
                {innerSuggestedEmployees ? (
                    <EmployeeSuggestions
                        id="selected"
                        flexDirection="column"
                        suggestedEmployees={innerSuggestedEmployees}
                    />
                ) : (
                    <LoadingBox />
                )}
            </List>
        </CardBox>
    );
}
export default SuggestedEmployeesBox;
